import { normalizeFileProcessingScopes, type ReleaseFileProcessing, type ReleaseFileProcessingMappingRule } from "@packages/commons";
import type {
  AdapterFileProcessingRouteDefinition,
  FileProcessingScopeKey,
} from "./types";

/** User-facing names for inbound file scopes; the persisted keys remain unchanged. */
export const fileProcessingScopeNames: Record<FileProcessingScopeKey, string> = {
  contacts: "Contact",
  entities: "Extension",
  orgmodels: "Org model",
  sessions: "Session",
};

const toScopeObject = (
  value: ReleaseFileProcessing[FileProcessingScopeKey] | undefined,
): { mapping: ReleaseFileProcessingMappingRule[]; [key: string]: unknown } => {
  if (Array.isArray(value)) return { mapping: value };
  if (value && typeof value === "object") {
    return {
      ...value,
      mapping: Array.isArray(value.mapping) ? value.mapping : [],
    };
  }
  return { mapping: [] };
};

export const getScopeMappings = (
  fileProcessing: ReleaseFileProcessing | undefined,
  scope: FileProcessingScopeKey,
): ReleaseFileProcessingMappingRule[] => toScopeObject(scope === "entities" ? normalizeFileProcessingScopes(fileProcessing)?.extensions : fileProcessing?.[scope]).mapping;

export const buildAdapterRouteId = (adapterId: string, routeKey: string): string => `adapter-${adapterId}-${routeKey}`;

export const buildAdapterFileProcessingRule = (
  adapterId: string,
  route: AdapterFileProcessingRouteDefinition,
  idsByMappingKey: Record<string, string> | undefined,
): ReleaseFileProcessingMappingRule | undefined => {
  const mapping = idsByMappingKey?.[route.mappingKey];
  if (!mapping) return undefined;

  let contactData: ReleaseFileProcessingMappingRule["contactData"];
  if (route.contactData) {
    const contactMapping = idsByMappingKey?.[route.contactData.mappingKey];
    if (!contactMapping) return undefined;
    const { mappingKey: _mappingKey, ...template } = route.contactData;
    contactData = { ...template, mapping: contactMapping };
  }

  return {
    id: buildAdapterRouteId(adapterId, route.key),
    mapping,
    ...(route.isDefault !== undefined ? { isDefault: route.isDefault } : {}),
    match: route.match ?? "",
    ...(route.batching ? { batching: { ...route.batching, groupBy: [...(route.batching.groupBy ?? [])] } } : {}),
    ...(contactData ? { contactData } : {}),
  };
};

const stringArraysEqual = (left: string[] | undefined, right: string[] | undefined): boolean =>
  JSON.stringify(left ?? []) === JSON.stringify(right ?? []);

export const adapterFileProcessingRulesEqual = (
  actual: ReleaseFileProcessingMappingRule | undefined,
  expected: ReleaseFileProcessingMappingRule | undefined,
): boolean => {
  if (!actual || !expected) return false;

  const batchingEqual =
    (!actual.batching && !expected.batching) ||
    (actual.batching?.mode === expected.batching?.mode &&
      stringArraysEqual(actual.batching?.groupBy, expected.batching?.groupBy) &&
      actual.batching?.maxConcurrency === expected.batching?.maxConcurrency &&
      actual.batching?.delayMs === expected.batching?.delayMs &&
      actual.batching?.failurePolicy === expected.batching?.failurePolicy);
  const contactDataEqual =
    (!actual.contactData && !expected.contactData) ||
    (actual.contactData?.enabled === expected.contactData?.enabled &&
      actual.contactData?.contactColumn === expected.contactData?.contactColumn &&
      (actual.contactData?.path ?? "") === (expected.contactData?.path ?? "") &&
      actual.contactData?.mapping === expected.contactData?.mapping &&
      Boolean(actual.contactData?.externalIdentifier) === Boolean(expected.contactData?.externalIdentifier) &&
      (actual.contactData?.externalIdentifierNamespace ?? "") ===
        (expected.contactData?.externalIdentifierNamespace ?? "") &&
      (actual.contactData?.externalIdentifierKey ?? "") === (expected.contactData?.externalIdentifierKey ?? ""));

  return (
    actual.id === expected.id &&
    actual.mapping === expected.mapping &&
    Boolean(actual.isDefault) === Boolean(expected.isDefault) &&
    (actual.match ?? "") === (expected.match ?? "") &&
    batchingEqual &&
    contactDataEqual
  );
};

export const mergeFileProcessingRoute = (
  fileProcessing: ReleaseFileProcessing | undefined,
  scope: FileProcessingScopeKey,
  rule: ReleaseFileProcessingMappingRule,
): ReleaseFileProcessing => {
  const source = scope === "entities" ? normalizeFileProcessingScopes(fileProcessing ?? {}) : fileProcessing ?? {};
  const key = scope === "entities" ? "extensions" : scope;
  const nextScope = toScopeObject(source[key]);
  const existingIndex = nextScope.mapping.findIndex((item) => item.id === rule.id);
  const mapping =
    existingIndex >= 0
      ? nextScope.mapping.map((item, index) => (
        index === existingIndex
          ? {
            ...rule,
            ...(item.index?.length ? { index: [...item.index] } : {}),
          }
          : item
      ))
      : [...nextScope.mapping, rule];
  const orderedMapping = [
    ...mapping.filter((item) => item.isDefault !== true),
    ...mapping.filter((item) => item.isDefault === true),
  ];

  return {
    ...source,
    [key]: {
      ...nextScope,
      mapping: orderedMapping,
    },
  };
};

export const setFileProcessingSessionGoal = (
  fileProcessing: ReleaseFileProcessing | undefined,
  sessionGoal: string,
): ReleaseFileProcessing => {
  const source = fileProcessing ?? {};
  const sessions = toScopeObject(source.sessions);

  return {
    ...source,
    sessions: {
      ...sessions,
      sessionGoal,
    },
  };
};

/** Removes the session goal previously managed by an adapter while preserving session routes. */
export const clearFileProcessingSessionGoal = (
  fileProcessing: ReleaseFileProcessing | undefined,
): ReleaseFileProcessing | undefined => {
  if (!fileProcessing) return fileProcessing;
  const sessions = toScopeObject(fileProcessing.sessions);
  delete sessions.sessionGoal;

  return {
    ...fileProcessing,
    sessions,
  };
};

export const removeFileProcessingRoute = (
  fileProcessing: ReleaseFileProcessing | undefined,
  scope: FileProcessingScopeKey,
  routeId: string,
): ReleaseFileProcessing | undefined => {
  if (!fileProcessing) return fileProcessing;

  const source = scope === "entities" ? normalizeFileProcessingScopes(fileProcessing) : fileProcessing;
  const key = scope === "entities" ? "extensions" : scope;
  const nextScope = toScopeObject(source[key]);
  if (!nextScope.mapping.some((item) => item.id === routeId)) return fileProcessing;

  return {
    ...source,
    [key]: {
      ...nextScope,
      mapping: nextScope.mapping.filter((item) => item.id !== routeId),
    },
  };
};
